export interface InvoiceTaxAdjustment {
  adjustmentType?: unknown;
  adjustment_type?: unknown;
  amount?: unknown;
  code?: unknown;
  label?: unknown;
  rate?: unknown;
  type?: unknown;
}

export interface InvoiceTaxSummaryRow {
  amount: number;
  code: string;
  label: string;
  ratePercent: number | null;
}

export interface InvoiceDocumentTaxSource {
  adjustments?: InvoiceTaxAdjustment[] | null;
  sourceOrder?: unknown;
  taxPst?: unknown;
}

export interface InvoiceDocumentTaxTotals {
  gst?: unknown;
  pst?: unknown;
  tax?: unknown;
}

const KNOWN_TAX_CODES = new Set(["GST", "PST", "HST", "QST", "RST", "TAX"]);

function clean(value: unknown) {
  return String(value || "").trim();
}

function money(value: unknown) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number * 100) / 100 : 0;
}

function taxCode(adjustment: InvoiceTaxAdjustment) {
  const direct = clean(adjustment.code).toUpperCase();
  if (direct && direct !== "TAX") return direct;

  const labelCode = clean(adjustment.label).toUpperCase().match(/\b(GST|PST|HST|QST|RST)\b/)?.[1];
  return labelCode || direct || "TAX";
}

function isTaxAdjustment(adjustment: InvoiceTaxAdjustment) {
  const type = clean(adjustment.type || adjustment.adjustmentType || adjustment.adjustment_type).toLowerCase();
  const code = taxCode(adjustment);
  return type === "tax" || KNOWN_TAX_CODES.has(code) || code.endsWith("_TAX");
}

function ratePercent(value: unknown) {
  const rate = Number(value);
  if (!Number.isFinite(rate) || rate <= 0) return null;
  const percent = rate <= 1 ? rate * 100 : rate;
  return Math.round(percent * 1000) / 1000;
}

function percentLabel(percent: number | null) {
  if (percent == null) return "";
  return Number.isInteger(percent) ? String(percent) : percent.toFixed(3).replace(/\.?0+$/, "");
}

function summaryLabel(code: string, sourceLabel: string, percent: number | null) {
  const label = code === "TAX" ? (sourceLabel || "Tax") : code;
  const rate = percentLabel(percent);
  return rate ? `${label} (${rate}%)` : label;
}

/**
 * Builds invoice-facing tax rows from stored order adjustments. Detailed rows
 * are grouped by tax code and rate; any unclassified remainder stays visible as
 * a generic Tax row so the displayed breakdown always reconciles to taxTotal.
 */
export function invoiceTaxSummaryRows(
  adjustments: InvoiceTaxAdjustment[] | null | undefined,
  taxTotal?: unknown
): InvoiceTaxSummaryRow[] {
  const grouped = new Map<string, InvoiceTaxSummaryRow & { sourceLabel: string }>();

  for (const adjustment of adjustments || []) {
    if (!adjustment || !isTaxAdjustment(adjustment)) continue;
    const amount = money(adjustment.amount);
    if (amount <= 0) continue;

    const code = taxCode(adjustment);
    const percent = ratePercent(adjustment.rate);
    const sourceLabel = clean(adjustment.label) || "Tax";
    const genericLabelKey = code === "TAX" ? sourceLabel.toUpperCase() : "";
    const key = `${code}:${percent == null ? "" : percent}:${genericLabelKey}`;
    const existing = grouped.get(key);

    if (existing) {
      existing.amount = money(existing.amount + amount);
      continue;
    }

    grouped.set(key, {
      amount,
      code,
      label: "",
      ratePercent: percent,
      sourceLabel
    });
  }

  let rows = [...grouped.values()].map(({ sourceLabel, ...row }) => ({
    ...row,
    label: summaryLabel(row.code, sourceLabel, row.ratePercent)
  }));

  const hasTaxTotal = taxTotal !== undefined && taxTotal !== null && Number.isFinite(Number(taxTotal));
  if (!hasTaxTotal) return rows;

  const expectedTotal = money(taxTotal);
  if (expectedTotal <= 0) return [];

  const detailedTotal = money(rows.reduce((sum, row) => sum + row.amount, 0));
  if (detailedTotal > expectedTotal) {
    return [{ amount: expectedTotal, code: "TAX", label: "Tax", ratePercent: null }];
  }

  const remainder = money(expectedTotal - detailedTotal);
  if (remainder > 0) {
    rows = [...rows, { amount: remainder, code: "TAX", label: "Tax", ratePercent: null }];
  }

  return rows;
}

export function invoiceDocumentTaxRows(
  invoice: InvoiceDocumentTaxSource | null | undefined,
  totals: InvoiceDocumentTaxTotals | null | undefined,
  legacyRates: { gst?: unknown; pst?: unknown } = {}
) {
  const adjustments = Array.isArray(invoice?.adjustments) ? invoice.adjustments : [];
  if (invoiceTaxSummaryRows(adjustments).length) {
    return invoiceTaxSummaryRows(adjustments, totals?.tax);
  }

  const isLegacyDemoInvoice = Boolean(
    invoice
    && !invoice.sourceOrder
    && Object.prototype.hasOwnProperty.call(invoice, "taxPst")
  );
  const legacyTaxRows = isLegacyDemoInvoice
    ? [
        { type: "tax", code: "GST", label: "GST", rate: legacyRates.gst ?? 0.05, amount: totals?.gst },
        { type: "tax", code: "PST", label: "PST", rate: legacyRates.pst ?? 0.07, amount: totals?.pst }
      ]
    : [];

  return invoiceTaxSummaryRows(legacyTaxRows, totals?.tax);
}
